import { Component } from '@angular/core'
import { DialogRef } from '@angular/cdk/dialog'
import { selectCases } from 'src/app/state/cases/cases.selectors'
import { combineLatest, map, Observable, take } from 'rxjs'
import { CaseTreeNode } from 'src/app/state/cases/cases.state'
import { Store } from '@ngrx/store'
import { selectAllSavedHierarchicalSelectionNodeItems } from 'src/app/state/selection/selection.selectors'
import { selectSavedFreeSelectionNames } from 'src/app/state/selection/selection.selectors'
import { environment } from 'src/environments/environment'
import { CaseId, SegObjectPropertyMap } from 'src/app/state/case/case.state'
import { HttpClient } from '@angular/common/http'
import { selectCaseId, selectCaseSegObjectPropertyMap } from 'src/app/state/case/case.selectors'

interface TreeNode {
    id: string
    show: boolean
    selected: boolean
    isTopLevel?: boolean
    nodes: TreeNode[]
    details?: any
}

@Component({
    selector: 'app-create-group-dialog',
    templateUrl: './create-group-dialog.component.html',
    styleUrls: ['./create-group-dialog.component.scss'],
})
export class CreateGroupDialogComponent {
    nodes: TreeNode[] = [
        {
            id: 'Cohort',
            show: false,
            selected: false,
            isTopLevel: true,
            nodes: [],
        },
        {
            id: 'Selections',
            show: false,
            selected: false,
            isTopLevel: true,
            nodes: [],
        },
        {
            id: 'Categorical property',
            show: false,
            selected: false,
            isTopLevel: true,
            nodes: [],
        },
    ]
    cohorts: { id: number; name: string }[] = []
    selectionNames$: Observable<{ name: string; type: number }[]>
    readonly currentSegObjectPropertyMap$ = this.store.select(selectCaseSegObjectPropertyMap)
    private readonly baseUrl = environment.apiUrl
    private caseId: CaseId

    constructor(
        private readonly dialogRef: DialogRef<any[]>,
        private readonly http: HttpClient,
        private readonly store: Store
    ) {
        this.store
            .select(selectCaseId)
            .pipe(take(1))
            .subscribe(caseId => {
                this.caseId = caseId
            })

        this.store
            .select(selectCases)
            .pipe(take(1))
            .subscribe((cases: CaseTreeNode[]) => {
                this.cohorts = cases
                    .filter(c => c.children?.length)
                    .map(c => ({ id: c.id, name: c.name }))

                const cohortNode = this.nodes.find(n => n.id === 'Cohort')
                if (cohortNode) {
                    cohortNode.nodes = this.cohorts.map(cohort => ({
                        id: cohort.name,
                        show: false,
                        selected: false,
                        nodes: [],
                    }))
                }
            })

        this.selectionNames$ = combineLatest([
            this.store.select(selectSavedFreeSelectionNames),
            this.store.select(selectAllSavedHierarchicalSelectionNodeItems),
        ]).pipe(
            take(1),
            map(([freeSelectionsName, hierarchicalSelectionsGroup]) => {
                const freeSelectionNames = freeSelectionsName.map(name => ({ name: name, type: 1 }))
                const hierarchicalNames = hierarchicalSelectionsGroup
                    .map(g => g.tree.map(t => `[${g.groupName}] ${t.name}`))
                    .flat()
                    .map(name => ({ name: name, type: 2 }))

                return [...freeSelectionNames, ...hierarchicalNames]
            })
        )

        this.selectionNames$.subscribe(s => {
            const selectionNode = this.nodes.find(n => n.id === 'Selections')
            if (selectionNode) {
                selectionNode.nodes = s.map(selection => ({
                    id: selection.name,
                    show: false,
                    selected: false,
                    nodes: [],
                }))
            }
        })

        this.currentSegObjectPropertyMap$.subscribe(segObjectPropertyMap => {
            if (segObjectPropertyMap) {
                this.http
                    .get<SegObjectPropertyMap[]>(
                        `${this.baseUrl}/datasets/${this.caseId}/categoricalProperties/${
                            segObjectPropertyMap.name
                        }?_=${Date.now()}`
                    )
                    .subscribe({
                        next: response => {
                            const categoricalPropertyNode = this.nodes.find(
                                n => n.id === 'Categorical property'
                            )
                            if (categoricalPropertyNode) {
                                categoricalPropertyNode.nodes = response.map(property => ({
                                    id: property.name,
                                    show: false,
                                    selected: false,
                                    nodes: [],
                                    details: property,
                                }))
                            }
                        },
                        error: error => {
                            console.error('Error fetching categorical properties:', error)
                        },
                    })
            }
        })
    }

    hasChildren(node: TreeNode): boolean {
        return node.nodes && node.nodes.length > 0
    }

    toggleNode(node: TreeNode) {
        node.show = !node.show
    }

    selectSelection(node: TreeNode) {
        node.selected = !node.selected

        if (node.isTopLevel && node.nodes.length > 0) {
            node.nodes.forEach(child => {
                child.selected = node.selected
            })
        }
    }

    cancel(): void {
        this.dialogRef.close(undefined)
    }

    confirm(): void {
        this.dialogRef.close(
            this.nodes
                .map(node => ({
                    id: node.id,
                    nodes: node.nodes
                        .filter(n => n.selected)
                        .map(n => ({
                            id: n.id,
                            details: n.details,
                        })),
                }))
                .filter(n => n.nodes.length > 0)
        )
    }
}
